import React, { useEffect, useState } from "react";
import { Search, RefreshCw, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

const STATUS_COLOR = {
  CAPTURED: "text-emerald-400", CREATED: "text-slate-400", FAILED: "text-rose-400",
  REFUNDED: "text-amber-400", NO_PAYMENT: "text-rose-400",
};

export default function Payments() {
  const [view, setView] = useState("list");
  const [pays, setPays] = useState([]);
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [recon, setRecon] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadList = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q; if (status) params.status = status;
      const { data } = await api.get("/admin/payments", { params });
      setPays(data.payments); setCounts(data.counts);
    } catch { toast.error("Could not load payments"); } finally { setLoading(false); }
  };
  const loadRecon = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/payments/reconcile"); setRecon(data); }
    catch { toast.error("Reconciliation failed"); } finally { setLoading(false); }
  };
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { if (view === "reconcile" && !recon) loadRecon(); /* eslint-disable-next-line */ }, [view]);

  return (
    <div className="space-y-5" data-testid="admin-payments">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-bold">Payments</h1>
        <div className="flex gap-1 rounded-full border border-slate-800 p-1">
          <button data-testid="payments-tab-list" onClick={() => setView("list")} className={`rounded-full px-4 py-1.5 text-sm font-medium ${view === "list" ? "bg-emerald-500 text-slate-950" : "text-slate-400"}`}>Payments</button>
          <button data-testid="payments-tab-reconcile" onClick={() => setView("reconcile")} className={`rounded-full px-4 py-1.5 text-sm font-medium ${view === "reconcile" ? "bg-emerald-500 text-slate-950" : "text-slate-400"}`}>Reconciliation</button>
        </div>
      </div>

      {view === "list" && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-full border border-slate-700 px-3 py-1.5">
              <Search className="h-4 w-4 text-slate-500" />
              <input data-testid="payments-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadList()} placeholder="Order / payment id / merchant" className="w-56 bg-transparent text-sm outline-none placeholder:text-slate-600" />
            </div>
            <select data-testid="payments-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm">
              <option value="">All</option><option value="CAPTURED">Captured</option><option value="CREATED">Created</option><option value="FAILED">Failed</option><option value="REFUNDED">Refunded</option>
            </select>
            <div className="flex gap-2 text-xs">
              {Object.entries(counts).map(([k, v]) => (
                <span key={k} className="rounded-full bg-slate-900 px-3 py-1.5 font-mono"><span className={STATUS_COLOR[k]}>{k}</span> {v}</span>
              ))}
            </div>
          </div>
          <div className="ledger-card overflow-hidden rounded-2xl">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payments-table">
                <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="p-4 text-left">Date</th><th className="p-4 text-left">Merchant</th><th className="p-4 text-left">Order</th><th className="p-4 text-right">Amount</th><th className="p-4 text-left">Status</th></tr>
                </thead>
                <tbody>
                  {pays.map((p) => (
                    <tr key={p.payment_id} className="border-t border-slate-800/60">
                      <td className="p-4 text-slate-400">{new Date(p.created_at).toLocaleString("en-IN")}</td>
                      <td className="p-4">{p.business_name}</td>
                      <td className="p-4 font-mono text-xs text-slate-500">{p.gatewayOrderId}</td>
                      <td className="p-4 text-right font-mono text-emerald-400">{p.amount_display}</td>
                      <td className={`p-4 font-mono text-xs ${STATUS_COLOR[p.status] || ""}`}>{p.status}</td>
                    </tr>
                  ))}
                  {loading && <tr><td colSpan={5} className="p-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-500" /></td></tr>}
                  {!loading && pays.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No payments.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {view === "reconcile" && (
        <div className="space-y-4" data-testid="payments-reconcile">
          <button onClick={loadRecon} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm hover:border-emerald-500/50"><RefreshCw className="h-4 w-4" /> Re-run</button>
          {!recon ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div> : (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <ReconStat label="Matched" value={recon.counts.matched} ok icon={CheckCircle2} />
                <ReconStat label="Mismatch" value={recon.counts.mismatch} warn={recon.counts.mismatch > 0} icon={AlertTriangle} />
                <ReconStat label="Needs review" value={recon.counts.review} warn={recon.counts.review > 0} icon={AlertTriangle} />
              </div>
              <div className="ledger-card rounded-2xl p-5" data-testid="budget-reconcile-card">
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Budget ledger vs guard</p>
                <div className="mt-2 flex flex-wrap items-center gap-6">
                  <span>Ledger: <span className="font-mono text-emerald-400">{recon.budget_reconcile.ledger_balance.display}</span></span>
                  <span>Guard: <span className="font-mono text-emerald-400">{recon.budget_reconcile.guard_balance.display}</span></span>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${recon.budget_reconcile.reconciled ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>{recon.budget_reconcile.reconciled ? "RECONCILED" : "MISMATCH"}</span>
                </div>
              </div>
              {(recon.mismatch.length > 0 || recon.review.length > 0) && (
                <div className="ledger-card overflow-hidden rounded-2xl">
                  <table className="w-full text-sm" data-testid="reconcile-issues-table">
                    <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500"><tr><th className="p-4 text-left">Merchant</th><th className="p-4 text-right">Amount</th><th className="p-4 text-left">Payment</th><th className="p-4 text-left">Sub</th><th className="p-4 text-left">Issue</th></tr></thead>
                    <tbody>
                      {[...recon.review, ...recon.mismatch].map((r, i) => (
                        <tr key={i} className="border-t border-slate-800/60">
                          <td className="p-4 font-mono text-xs">{r.merchant_id}</td>
                          <td className="p-4 text-right font-mono">{r.amount_display}</td>
                          <td className={`p-4 font-mono text-xs ${STATUS_COLOR[r.status] || ""}`}>{r.status}</td>
                          <td className="p-4 font-mono text-xs text-slate-400">{r.sub_status || "—"}</td>
                          <td className="p-4 text-xs text-amber-300">{r.issue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {recon.counts.mismatch === 0 && recon.counts.review === 0 && (
                <div className="ledger-card rounded-2xl p-8 text-center text-emerald-400"><CheckCircle2 className="mx-auto mb-2 h-8 w-8" />All payments reconcile with subscriptions & ledger.</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ReconStat({ label, value, ok, warn, icon: Icon }) {
  return (
    <div className={`ledger-card flex items-center gap-3 rounded-2xl p-5 ${warn ? "border-rose-500/30" : ""}`}>
      <Icon className={`h-6 w-6 ${warn ? "text-rose-400" : ok ? "text-emerald-400" : "text-slate-400"}`} />
      <div><p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</p><p className="font-heading text-2xl font-bold">{value}</p></div>
    </div>
  );
}
